/**
 * Header menus, drawer, and sticky height. Loaded from sections/header.liquid as type="module".
 */

const SELECTOR = '[data-section-type="header"]'
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

function trapFocus(container, event) {
  const focusable = [...container.querySelectorAll(FOCUSABLE)].filter(
    (element) => !element.hidden && element.offsetParent !== null
  )
  if (!focusable.length) return

  const first = focusable[0]
  const last = focusable[focusable.length - 1]

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}

function initDisclosure(disclosure) {
  if (disclosure.dataset.initialized === 'true') return

  const toggle = disclosure.querySelector('[data-disclosure-toggle]')
  const list = disclosure.querySelector('[data-disclosure-list]')
  const input = disclosure.querySelector('[data-disclosure-input]')
  const form = disclosure.closest('form')
  if (!toggle || !list || !input || !form) return

  function positionList() {
    const rect = toggle.getBoundingClientRect()
    list.style.top = `${Math.round(rect.bottom + 8)}px`
    list.style.right = `${Math.round(window.innerWidth - rect.right)}px`
    list.style.left = 'auto'
  }

  function close() {
    toggle.setAttribute('aria-expanded', 'false')
    list.setAttribute('aria-hidden', 'true')
    list.classList.add('hidden')
    list.style.top = ''
    list.style.right = ''
    list.style.left = ''
  }

  function open() {
    positionList()
    toggle.setAttribute('aria-expanded', 'true')
    list.setAttribute('aria-hidden', 'false')
    list.classList.remove('hidden')
  }

  toggle.addEventListener('click', () => {
    if (toggle.getAttribute('aria-expanded') === 'true') close()
    else open()
  })

  list.querySelectorAll('[data-disclosure-option]').forEach((option) => {
    option.addEventListener('click', (event) => {
      event.preventDefault()
      input.value = option.dataset.value
      close()
      form.submit()
    })
  })

  document.addEventListener('click', (event) => {
    if (!disclosure.contains(event.target)) close()
  })

  window.addEventListener('resize', () => {
    if (toggle.getAttribute('aria-expanded') === 'true') positionList()
  })

  disclosure.dataset.initialized = 'true'
  close()
}

function initHeader(header) {
  if (header.dataset.initialized === 'true') return

  const drawer = header.querySelector('[data-drawer-menu]')
  const mobileHeaderRow = header.querySelector('.header__row-mobile')
  const lowerBar = header.querySelector('.header__row-desktop.lower')
  const announcementBar = document.querySelector('.announcement-bar__outer-wrapper')
  const headerSpace = document.querySelector('[data-header-space]')
  const menuButtons = header.querySelectorAll('[data-js-menu-button]')
  const overlay = header.querySelector('.header__overlay')
  const quickSearch = document.querySelector('[data-quick-search-desktop]')
  let closeDrawerTimer = null

  document.documentElement.classList.add('sticky-header-enabled')
  document.body.classList.remove('header-transparent')

  function positionOverlay() {
    if (overlay) overlay.style.top = `${Math.ceil(header.getBoundingClientRect().bottom)}px`
  }

  function setHeaderHeight() {
    const height = Math.ceil(header.getBoundingClientRect().height)
    const lowerBarHeight = lowerBar ? Math.ceil(lowerBar.getBoundingClientRect().height) : height
    const lowerBarOffset = Math.max(height - lowerBarHeight, 0)

    document.documentElement.style.setProperty('--height-header', `${height}px`)
    document.documentElement.style.setProperty('--header-desktop-sticky-position', `${lowerBarOffset * -1}px`)
    document.documentElement.style.setProperty('--header-desktop-sticky-height', `${height - lowerBarOffset}px`)

    if (header.classList.contains('dropdown-active')) positionOverlay()
  }

  function setMenuButtonState(isOpen) {
    menuButtons.forEach((button) => {
      button.setAttribute('aria-expanded', String(isOpen))
      button.setAttribute(
        'aria-label',
        button.getAttribute(isOpen ? 'data-aria-label-opened' : 'data-aria-label-closed')
      )
      button.querySelector('.icon-header-menu')?.toggleAttribute('hidden', isOpen)
      button.querySelector('.icon-header-menu-close')?.toggleAttribute('hidden', !isOpen)
    })
  }

  function setOverlayMenuOpen(menu, isOpen) {
    menu.classList.toggle('hidden', !isOpen)
    menu.toggleAttribute('hidden', !isOpen)
    menu.setAttribute('aria-hidden', isOpen ? 'false' : 'true')
    menu.setAttribute('data-hidden', isOpen ? 'false' : 'true')
    menu.inert = !isOpen
  }

  function setDrawerHidden(isHidden) {
    if (!drawer) return
    drawer.classList.toggle('hidden', isHidden)
    drawer.toggleAttribute('hidden', isHidden)
    drawer.inert = isHidden
  }

  function resetDrawerPosition() {
    drawer.style.top = ''
    drawer.style.bottom = ''
    drawer.style.height = ''
  }

  function closeDrawer() {
    if (!drawer) return
    drawer.classList.remove('active')
    drawer.setAttribute('aria-hidden', 'true')
    drawer.inert = true
    document.body.removeAttribute('mobile-menu-open')
    document.documentElement.classList.remove('overflow-hidden')
    document.body.classList.remove('overflow-hidden')
    mobileHeaderRow?.classList.remove('opacity-0', 'pointer-events-none')
    mobileHeaderRow?.removeAttribute('inert')
    // Keep the open-state top offset until the slide-out finishes, otherwise the
    // drawer jumps upward mid-animation when inset-0 takes over. Then remove the
    // drawer from the layout so lazy images are not fetched while it is closed.
    window.clearTimeout(closeDrawerTimer)
    closeDrawerTimer = window.setTimeout(() => {
      resetDrawerPosition()
      setDrawerHidden(true)
    }, 300)
    setMenuButtonState(false)
  }

  function openDrawer() {
    if (!drawer) return
    window.clearTimeout(closeDrawerTimer)
    setDrawerHidden(false)
    // Force a layout pass so the slide-in transition still runs after display:none.
    void drawer.offsetHeight
    drawer.classList.add('active')
    drawer.setAttribute('aria-hidden', 'false')
    document.body.setAttribute('mobile-menu-open', 'true')
    document.documentElement.classList.add('overflow-hidden')
    document.body.classList.add('overflow-hidden')
    mobileHeaderRow?.classList.add('opacity-0', 'pointer-events-none')
    mobileHeaderRow?.setAttribute('inert', '')
    // Start the drawer below the announcement bar. Bound height to the remaining
    // viewport so the inner overflow-y scroller can actually scroll.
    const offset = announcementBar ? Math.max(0, Math.round(announcementBar.getBoundingClientRect().bottom)) : 0
    drawer.style.top = `${offset}px`
    drawer.style.bottom = '0'
    drawer.style.height = `calc(100dvh - ${offset}px)`
    setMenuButtonState(true)
    drawer.querySelector('a, button, input, select')?.focus()
  }

  function setSearchButtonState(isOpen) {
    header.querySelectorAll('[data-search]').forEach((button) => {
      button.setAttribute('aria-expanded', String(isOpen))
      button.querySelector('.icon-header-search')?.toggleAttribute('hidden', isOpen)
      button.querySelector('.icon-header-menu-close')?.toggleAttribute('hidden', !isOpen)
    })
  }

  function closeQuickSearch() {
    if (!quickSearch) return
    quickSearch.classList.add('hidden')
    quickSearch.setAttribute('aria-hidden', 'true')
    document.body.classList.remove('overflow-hidden')
    setSearchButtonState(false)
  }

  function openQuickSearch() {
    if (!quickSearch) return
    closeDrawer()
    quickSearch.classList.remove('hidden')
    quickSearch.setAttribute('aria-hidden', 'false')
    document.body.classList.add('overflow-hidden')
    setSearchButtonState(true)
    quickSearch.querySelector('[data-input]')?.focus()
  }

  header.querySelectorAll('[data-search]').forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault()
      openQuickSearch()
    })
  })
  quickSearch?.querySelectorAll('[data-overlay], [data-close-icon]').forEach((button) => {
    button.addEventListener('click', closeQuickSearch)
  })
  quickSearch?.querySelector('[data-clear]')?.addEventListener('click', () => {
    const input = quickSearch.querySelector('[data-input]')
    if (input) input.value = ''
    input?.focus()
  })

  menuButtons.forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault()
      if (drawer?.classList.contains('active')) closeDrawer()
      else openDrawer()
    })
  })

  drawer?.querySelector('[data-overlay]')?.addEventListener('click', closeDrawer)
  drawer?.querySelectorAll('[data-mobile-nav-close]').forEach((button) => {
    button.addEventListener('click', closeDrawer)
  })
  drawer?.querySelectorAll('select[name="country_code"], select[name="locale_code"]').forEach((select) => {
    select.addEventListener('change', () => {
      closeDrawer()
      select.form?.submit()
    })
  })

  drawer?.querySelectorAll('[data-mobile-nav]').forEach((mobileNav) => {
    const tabs = [...mobileNav.querySelectorAll('[data-mobile-nav-tab]')]
    const panels = [...mobileNav.querySelectorAll('[data-mobile-nav-panel]')]

    function setActiveTab(tabId) {
      tabs.forEach((tab) => {
        const isActive = tab.dataset.tabId === tabId
        tab.setAttribute('aria-selected', String(isActive))
        tab.tabIndex = isActive ? 0 : -1
      })
      panels.forEach((panel) => {
        const isInactive = panel.dataset.tabId !== tabId
        panel.hidden = isInactive
        panel.classList.toggle('!hidden', isInactive)
      })
    }

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => setActiveTab(tab.dataset.tabId))
    })

    const activeTab = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0]
    if (activeTab) setActiveTab(activeTab.dataset.tabId)
  })

  const topLevelLinks = header.querySelectorAll('[data-navigation] > .header__links-list > li > [data-link]')

  function setNavigationLinkState(activeLink) {
    topLevelLinks.forEach((link) => {
      const isActive = link === activeLink
      link.classList.toggle('opacity-40', Boolean(activeLink) && !isActive)
      link.classList.toggle('is-nav-hover', isActive)
    })
  }

  function closeNavigation(exception) {
    header.querySelectorAll('[data-submenu]').forEach((menu) => {
      if (menu === exception || menu.contains(exception)) return
      setOverlayMenuOpen(menu, false)
    })
    header.querySelectorAll('[data-parent], [data-dropdown-trigger]').forEach((trigger) => {
      if (!exception || trigger.getAttribute('aria-controls') !== exception.id) {
        trigger.setAttribute('aria-expanded', 'false')
      }
    })
    if (!exception) {
      header.classList.remove('dropdown-active')
      overlay?.classList.remove('opacity-50')
      setNavigationLinkState()
    }
  }

  function openNavigation(trigger) {
    const menuId = trigger.getAttribute('aria-controls')
    const menu = (menuId ? document.getElementById(menuId) : null) || trigger.nextElementSibling
    if (!menu) return

    closeNavigation(menu)
    if (menu.hasAttribute('data-meganav') || menu.dataset.depth === '1') {
      menu.style.top = `${header.getBoundingClientRect().bottom}px`
    }
    setOverlayMenuOpen(menu, true)
    const alreadyOpen = trigger.getAttribute('aria-expanded') === 'true'
    trigger.setAttribute('aria-expanded', 'true')
    if (!alreadyOpen && window.oneskin?.events) {
      window.oneskin.events.track('nav_expand', {
        nav_location: 'header_primary',
        device_type: window.oneskin.events.deviceType(),
      })
    }
    header.classList.add('dropdown-active')
    positionOverlay()
    overlay?.classList.add('opacity-50')
    setNavigationLinkState(trigger)
  }

  header.querySelectorAll('[data-navigation]').forEach((nav) => {
    nav.querySelectorAll('[data-parent], [data-dropdown-trigger]').forEach((trigger) => {
      trigger.addEventListener('mouseenter', () => openNavigation(trigger))
      trigger.addEventListener('focus', () => openNavigation(trigger))
      if (trigger.tagName === 'BUTTON') {
        trigger.addEventListener('click', () => openNavigation(trigger))
      }
    })

    nav.querySelectorAll(':scope > .header__links-list > li > [data-link]').forEach((link) => {
      if (link.matches('[data-parent], [data-dropdown-trigger]')) return
      link.addEventListener('mouseenter', () => {
        closeNavigation()
        setNavigationLinkState(link)
      })
      link.addEventListener('focus', () => {
        closeNavigation()
        setNavigationLinkState(link)
      })
    })
  })

  header.addEventListener('mouseleave', () => closeNavigation())
  document.addEventListener('click', (event) => {
    if (!header.contains(event.target)) closeNavigation()
  })
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Tab') {
      if (drawer?.classList.contains('active')) trapFocus(drawer, event)
      if (quickSearch && !quickSearch.classList.contains('hidden')) trapFocus(quickSearch, event)
    }
    if (event.key !== 'Escape') return
    closeDrawer()
    closeQuickSearch()
    closeNavigation()
  })

  header.querySelectorAll('[data-disclosure]').forEach(initDisclosure)

  if (headerSpace) {
    new IntersectionObserver(([{ isIntersecting }]) => {
      header.classList.toggle('is-sticky', !isIntersecting)
      document.documentElement.classList.toggle('sticky-header-active', !isIntersecting)
    }).observe(headerSpace)
  }

  setHeaderHeight()
  new ResizeObserver(setHeaderHeight).observe(header)
  matchMedia('(min-width: 1025px)').addEventListener('change', (event) => {
    if (event.matches) closeDrawer()
  })

  header.dataset.initialized = 'true'
}

function initAll(root = document) {
  root.querySelectorAll(SELECTOR).forEach(initHeader)
}

initAll()

document.addEventListener('shopify:section:load', (event) => {
  initAll(event.target)
})

window.addEventListener('keydown', (event) => {
  if (event.key === 'Tab' && !['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)) {
    document.body.classList.add('user-is-tabbing')
  }
})
window.addEventListener('mousedown', () => {
  document.body.classList.remove('user-is-tabbing')
})
